/**
 * The words for the shared workflow read model.
 *
 * Same rule as `i18n/terms.ts` and for the same reason: `not_applicable`,
 * `prepare_videos` and `quality_issues` are wire values, and nothing here
 * lets one reach a screen. This file is the single boundary where the
 * backend's evaluated workflow becomes a sentence, so the checklist on the
 * project screen, the next action on a card and the attention list on Ana
 * Sayfa are guaranteed to describe the same state in the same words.
 *
 * Three rules the tests hold this file to:
 *
 * 1. **`todo` never reads as a failure.** It means nobody has measured yet.
 *    Its tone is neutral, its sentence is an invitation, and it never gets a
 *    warning colour. Only `attention` - which always has a count or a status
 *    behind it - is allowed to look like something is wrong.
 *
 * 2. **`not_applicable` is neutral, not negative.** A room project has no
 *    five-region head coverage. That is a fact about the subject, not a gap
 *    in the project, and the sentence says which.
 *
 * 3. **Nothing here can say "3D Model hazır" from a job status.** The final
 *    model step reads `has_final_model`, which the backend sets only from a
 *    validated mesh artifact.
 */

import type {
  SubjectType,
  WorkflowActionKey,
  WorkflowAttentionKey,
  WorkflowStep,
  WorkflowStepKey,
  WorkflowStepState,
} from "@/api/types";

/** How a state should look. `todo` and `not_applicable` are both neutral. */
export type WorkflowTone = "success" | "caution" | "neutral";

export const STEP_STATE_TONE: Record<WorkflowStepState, WorkflowTone> = {
  done: "success",
  attention: "caution",
  // Not measured yet. The ordinary state of new work.
  todo: "neutral",
  not_applicable: "neutral",
  unavailable: "neutral",
};

/**
 * The mark next to a step. Deliberately three shapes, not three colours:
 * status must survive a monochrome screenshot and a colour-blind reader.
 */
export const STEP_STATE_MARK: Record<WorkflowStepState, string> = {
  done: "✓",
  attention: "!",
  todo: "○",
  not_applicable: "–",
  unavailable: "–",
};

/** Read aloud instead of the mark, so the icon is never the only carrier. */
export const STEP_STATE_LABEL: Record<WorkflowStepState, string> = {
  done: "Tamamlandı",
  attention: "Dikkat gerekiyor",
  todo: "Henüz yapılmadı",
  not_applicable: "Bu proje türü için geçerli değil",
  unavailable: "Bu kurulumda kullanılamıyor",
};

const STEP_TITLES: Record<WorkflowStepKey, string> = {
  media: "Medya mevcut",
  quality: "Kalite kontrolü",
  head_analysis: "Baş analizi",
  coverage: "Kapsama değerlendirmesi",
  video_preparation: "Videolar hazırlandı",
  sparse: "Seyrek 3D",
  final_model: "Final 3D model",
};

export function stepTitle(key: WorkflowStepKey): string {
  return STEP_TITLES[key];
}

/**
 * The sentence under one checklist line.
 *
 * Built from the step's own measured numbers, so "6 dosyada kalite sorunu"
 * only ever appears when six is a count the backend produced. Where there is
 * no number, the sentence says what the state means and stops.
 */
export function stepDetail(step: WorkflowStep, subjectType: SubjectType): string {
  const { key, state, count, total } = step;

  if (state === "not_applicable") {
    if (key === "head_analysis" || key === "coverage") {
      return subjectType === "human_head"
        ? "Bu adım bu projede uygulanmıyor."
        : "Beş bölgeli baş kapsaması yalnızca insan başı projelerinde ölçülür; bu proje türü için authoritative bir kapsama sonucu henüz üretilmiyor.";
    }
    if (key === "video_preparation") return "Bu projede video yok.";
    return "Bu adım bu proje için geçerli değil.";
  }

  switch (key) {
    case "media":
      return state === "done"
        ? `${count ?? 0} dosya yüklendi.`
        : "Henüz dosya yok. Telefonla çekim yapabilir veya dosya yükleyebilirsiniz.";
    case "quality":
      if (state === "attention") return `${count ?? 0} dosyada kalite sorunu bulundu.`;
      if (state === "done") return `${total ?? count ?? 0} dosyanın tamamı değerlendirildi.`;
      return total && count
        ? `${total} dosyanın ${count} tanesi değerlendirildi.`
        : "Dosyalar henüz değerlendirilmedi.";
    case "head_analysis":
      return state === "done"
        ? "Yüz ve baş açısı ölçümleri yapıldı."
        : "Kareler henüz analiz edilmedi.";
    case "coverage":
      if (state === "done") {
        return count !== null
          ? `Ölçülen ${count} bölgede kapsama yeterli.`
          : "Ölçülen bölgelerde kapsama yeterli.";
      }
      if (state === "attention") {
        return count !== null
          ? `${count} bölge ölçüldü; en az biri için ek çekim gerekiyor.`
          : "Ölçülen bölgelerden bazıları için ek çekim gerekiyor.";
      }
      return "Kapsama henüz hesaplanmadı.";
    case "video_preparation":
      if (state === "done") return `${total ?? 0} videonun tamamı hazırlandı.`;
      return `${total ?? 0} videonun ${count ?? 0} tanesi hazırlandı.`;
    case "sparse":
      if (state === "done") return "Seyrek nokta bulutu oluşturuldu.";
      if (state === "attention") return "Son rekonstrüksiyon başarısız oldu.";
      return "Rekonstrüksiyon henüz çalıştırılmadı.";
    case "final_model":
      // The one sentence that must never be optimistic. A sparse cloud is a
      // real result and is not a model; saying so plainly is the point.
      return state === "done"
        ? "Doğrulanmış bir 3D model çıktısı mevcut."
        : "Doğrulanmış bir mesh çıktısı henüz yok. Seyrek 3D bir model değildir.";
  }
}

/** The headline of the next-action panel. One sentence, no percentages. */
export const ACTION_TITLES: Record<WorkflowActionKey, string> = {
  add_capture: "Çekim ekleyin.",
  phone_capture: "Telefonla çekim yapın.",
  upload_media: "Dosya yükleyin.",
  run_analysis: "Medyanızı analiz edin.",
  prepare_videos: "Videoları rekonstrüksiyon için hazırlayın.",
  measure_coverage: "Kapsama değerlendirmesini çalıştırın.",
  add_correction_capture: "Eksik bölgeler için düzeltme çekimi ekleyin.",
  start_reconstruction: "3D rekonstrüksiyonu başlatabilirsiniz.",
  view_reconstruction: "Rekonstrüksiyon durumunu görüntüleyin.",
  view_outputs: "Seyrek 3D hazır.",
  review_quality: "Kalite sorunlarını gözden geçirin.",
};

/** The button. Absent for informational actions, which have nothing to press. */
export const ACTION_BUTTONS: Record<WorkflowActionKey, string> = {
  add_capture: "Çekim Ekle",
  phone_capture: "Telefonla Çek",
  upload_media: "Dosya Yükle",
  run_analysis: "Analize Git",
  prepare_videos: "Videoları Hazırla",
  measure_coverage: "Kapsamaya Git",
  add_correction_capture: "Düzeltme Çekimi Ekle",
  start_reconstruction: "Rekonstrüksiyona Git",
  view_reconstruction: "Rekonstrüksiyonu Görüntüle",
  view_outputs: "Çıktıları Görüntüle",
  review_quality: "Analize Git",
};

/**
 * The sentence for an attention item.
 *
 * Every one takes a count or describes a terminal status, because every
 * `AttentionKey` the backend emits has evidence behind it. There is no
 * "bu projede eksikler var" here and there must never be one - a warning
 * without a number is a warning nobody can act on.
 */
export function attentionText(key: WorkflowAttentionKey, count: number | null): string {
  switch (key) {
    case "reconstruction_failed":
      return "Son rekonstrüksiyon başarısız oldu.";
    case "media_failed":
      return `${count ?? 0} dosya işlenemedi.`;
    case "quality_issues":
      return `${count ?? 0} dosyada kalite sorunu var.`;
    case "unprepared_videos":
      return `${count ?? 0} video rekonstrüksiyon için hazırlanmadı.`;
    case "coverage_insufficient":
      return "Ölçülen bölgelerden bazıları için ek çekim gerekiyor.";
  }
}

/** Which project tab an attention item is fixed on. */
export const ATTENTION_DESTINATION: Record<
  WorkflowAttentionKey,
  "sessions" | "reconstruction" | "coverage"
> = {
  reconstruction_failed: "reconstruction",
  media_failed: "sessions",
  quality_issues: "sessions",
  unprepared_videos: "reconstruction",
  coverage_insufficient: "coverage",
};
